'use client'

import { useState, useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { createClient } from '@/lib/supabase/client'
import { publicFirstName } from '@/lib/public-name'
import { Button } from '@/components/ui/button'
import { User, Send, CornerDownRight } from 'lucide-react'
import { UgcActionsMenu } from '@/components/moderation/ugc-actions-menu'
import { useBlockedUsers } from '@/lib/moderation/use-blocked-users'

interface Comment {
  id: string
  user_id: string
  content_id: string
  body: string
  status: string | null
  author_name: string | null
  parent_id: string | null
  created_at: string
  edited_at: string | null
  profiles: {
    display_name: string | null
    avatar_url: string | null
    /**
     * True when the author is staff (admin/moderator/content/support), used to
     * render the "Staff" pill next to the author name so readers can tell
     * official replies from fellow community members. Computed server-side by
     * the public_profiles view (COALESCE(role IN (...), false)) — the raw role
     * is never exposed to clients. NULL/false = regular member; no pill.
     */
    is_staff: boolean | null
  }
}

type CommentNode = Comment & { children: CommentNode[] }

/**
 * Cap on how deep the reply tree is allowed to NEST before it flattens.
 *
 * The thread renders recursively — `CommentThread` renders each child as
 * another `CommentThread`, and `buildCommentTree`'s sort walks the tree — so an
 * unbounded reply chain recurses once per level. A pathologically deep chain
 * (e.g. a bot, or a member replying to their own PENDING reply over and over —
 * those rows are visible only to their author via the own-pending RLS policy,
 * so they never need moderation to pile up) overran the JS call stack and threw
 * `RangeError: Maximum call stack size exceeded` on iOS/WebKit, whose stack is
 * far smaller than V8's — so it rendered on desktop but crashed on iPhone
 * (THEDUCHY-WEBSITE-55). Beyond this depth, descendants render FLAT at the cap
 * (no content is dropped) instead of nesting further. Real human threads never
 * approach this — indentation already stops at `maxIndent` (4) — so normal
 * conversations look identical. Mirrors PortableTextRenderer's
 * MAX_PORTABLE_TEXT_DEPTH guard.
 */
const MAX_COMMENT_DEPTH = 8

/**
 * All descendants of `nodes`, breadth-first, as a FLAT list. Iterative (never
 * recursive — this runs precisely when the tree is too deep to recurse) and
 * cycle-guarded, so a malformed parent chain can't loop. Used to render the
 * tail of an over-deep thread without nesting past the stack limit.
 */
function flattenReplies(nodes: CommentNode[]): CommentNode[] {
  const out: CommentNode[] = []
  const queue: CommentNode[] = [...nodes]
  const seen = new Set<string>()
  while (queue.length > 0) {
    const node = queue.shift()!
    if (seen.has(node.id)) continue
    seen.add(node.id)
    out.push(node)
    for (const child of node.children) queue.push(child)
  }
  return out
}

/** Inline author-edit wiring, threaded down the (recursive) comment tree. */
interface CommentEdit {
  editingId: string | null
  body: string
  saving: boolean
  error: string
  onChange: (v: string) => void
  onStart: (c: Comment) => void
  onSave: (id: string) => void
  onCancel: () => void
}

interface CommentsProps {
  /**
   * Stable content_index.id (uuid). This is the join key for comments —
   * unaffected by Sanity slug renames. Caller pages already resolve
   * slug → content_index.id server-side for likes/bookmarks/views; pass
   * the same uuid here. If the slug doesn't resolve to a content_index
   * row at all (gated content the visitor can't see, or a content type
   * with no index row), pass null and the section renders inert.
   */
  contentId: string | null
  userId?: string | null
}

function buildCommentTree(comments: Comment[]): CommentNode[] {
  const map = new Map<string, CommentNode>()
  const roots: CommentNode[] = []

  for (const c of comments) {
    map.set(c.id, { ...c, children: [] })
  }

  for (const c of comments) {
    const node = map.get(c.id)!
    // `c.parent_id !== c.id` drops a self-referential parent (a comment whose
    // parent is itself) — left in, it would push the node into its own
    // children, i.e. a cycle, and also strand it (a self-parent is never a
    // root). Treating it as a root keeps it visible and keeps the tree acyclic.
    if (c.parent_id && c.parent_id !== c.id && map.has(c.parent_id)) {
      map.get(c.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  // Top-level comments stay newest-first (query order).
  // Replies within a thread should read chronologically (oldest first).
  //
  // Iterative (explicit stack), NOT recursive: this runs inside render, and a
  // recursive walk would overflow the JS stack on a pathologically deep reply
  // chain BEFORE the component ever gets to apply its own depth cap — the same
  // iOS/WebKit crash the render guard fixes (THEDUCHY-WEBSITE-55). The `seen`
  // set makes it cycle-proof as a belt-and-suspenders to the self-parent guard
  // above.
  const stack: CommentNode[] = [...roots]
  const sortSeen = new Set<string>()
  while (stack.length > 0) {
    const node = stack.pop()!
    if (sortSeen.has(node.id)) continue
    sortSeen.add(node.id)
    if (node.children.length > 1) {
      node.children.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
    }
    for (const child of node.children) stack.push(child)
  }

  return roots
}

export function Comments({ contentId, userId }: CommentsProps) {
  const supabase = createClient()
  const t = useTranslations('comments')
  const [comments, setComments] = useState<Comment[]>([])
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  // Blocked authors' comments are hidden from the thread (unblock via
  // Account → Blocked users). Loads async; empty until then.
  const { blockedIds, onBlockChange } = useBlockedUsers()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')

  const fetchComments = useCallback(async () => {
    // No content_index row resolved upstream — render empty quietly
    // rather than fetching every comment in the table.
    if (!contentId) {
      setComments([])
      setLoading(false)
      return
    }

    // Fetch published comments for everyone, PLUS the viewer's OWN pending
    // comments so an author still sees their just-posted or just-edited comment
    // (with a "Pending review" badge) instead of it vanishing — the RLS
    // content_comments_select_merged policy already permits exactly this
    // (published, or status='pending' AND user_id = auth.uid()).
    let query = supabase
      .from('content_comments')
      .select(`
        id, user_id, content_id, body, status, author_name, parent_id, created_at, edited_at,
        profiles:public_profiles!user_id ( display_name, avatar_url, is_staff )
      `)
      .eq('content_id', contentId)
    query = userId
      ? query.or(`status.eq.published,and(status.eq.pending,user_id.eq.${userId})`)
      : query.eq('status', 'published')

    const { data } = await query.order('created_at', { ascending: false })

    if (data) {
      setComments(data as unknown as Comment[])
    }
    setLoading(false)
  }, [supabase, contentId, userId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount/refetch from Supabase
    fetchComments()
  }, [fetchComments])

  const handleSubmit = useCallback(async () => {
    if (!body.trim() || !userId || !contentId) return
    setSubmitting(true)
    setError('')

    // One shared route owns spam-check (server-side, unskippable) + the insert;
    // RLS + the escalation guard still force status='pending'. The author's own
    // pending row comes back on refetch with the "Pending review" badge.
    try {
      const res = await fetch('/api/engagement/comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_id: contentId,
          body: body.trim(),
          parent_id: replyTo?.id ?? null,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || 'Could not post your comment. Please try again.')
        setSubmitting(false)
        return
      }
    } catch {
      setError('Could not post your comment. Please try again.')
      setSubmitting(false)
      return
    }

    setBody('')
    setReplyTo(null)
    setSubmitting(false)
    fetchComments()
  }, [body, userId, contentId, replyTo, fetchComments])

  // Author self-edit. content_comments are moderate-before-publish, so the
  // content_comments_escalation_guard trigger flips an edited comment back to
  // 'pending' (re-moderation) — the author keeps seeing it (own-pending fetch
  // above) with a "Pending review" badge until a moderator re-approves.
  const startEdit = useCallback((c: Comment) => {
    setEditingId(c.id)
    setEditBody(c.body)
    setEditError('')
  }, [])
  const cancelEdit = useCallback(() => {
    setEditingId(null)
    setEditBody('')
    setEditError('')
  }, [])
  const saveEdit = useCallback(
    async (id: string) => {
      const text = editBody.trim()
      if (!text) return
      setEditSaving(true)
      setEditError('')
      // Spam check (fail open on an Akismet/network error; the re-moderation
      // queue is the real gate for content comments either way).
      try {
        const spamRes = await fetch('/api/spam-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: text, surface: 'content_comment_edit' }),
        })
        if (spamRes.ok && (await spamRes.json()).isSpam) {
          setEditError('Your edit was flagged as spam. Please revise it.')
          setEditSaving(false)
          return
        }
      } catch {
        // Akismet unavailable — allow through.
      }
      const { error: updErr } = await supabase
        .from('content_comments')
        .update({ body: text, edited_at: new Date().toISOString() })
        .eq('id', id)
      if (updErr) {
        setEditError(updErr.message)
        setEditSaving(false)
        return
      }
      setEditingId(null)
      setEditBody('')
      setEditSaving(false)
      fetchComments()
    },
    [editBody, supabase, fetchComments],
  )

  const editController: CommentEdit = {
    editingId,
    body: editBody,
    saving: editSaving,
    error: editError,
    onChange: setEditBody,
    onStart: startEdit,
    onSave: (id) => void saveEdit(id),
    onCancel: cancelEdit,
  }

  // Prefer the commenter's CURRENT profile name (live via public_profiles) so
  // renames and GDPR erasure propagate automatically; fall back to the stored
  // author_name only for guests / deleted users. publicFirstName strips
  // patreon_<id>/email and renders first-name-only (no last-name PII).
  const getDisplayName = (comment: Comment) =>
    publicFirstName(comment.profiles?.display_name, comment.author_name)

  const tree = buildCommentTree(comments.filter((c) => !blockedIds.has(c.user_id)))

  return (
    <div className="mt-12 border-t pt-8" data-testid="comments-section">
      <h2 className="text-xl font-bold text-gray-900 mb-6">
        {t('heading')} {comments.length > 0 && <span className="text-base font-normal text-gray-500 dark:text-gray-400">({comments.length})</span>}
      </h2>

      {/* Comment list */}
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('loading')}</p>
      ) : tree.length > 0 ? (
        <div className="space-y-4 mb-8">
          {tree.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment}
              depth={0}
              getDisplayName={getDisplayName}
              onReply={userId ? (id, name) => setReplyTo({ id, name }) : undefined}
              currentUserId={userId}
              onBlockChange={onBlockChange}
              edit={editController}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">{t('empty')}</p>
      )}

      {/* Add comment form */}
      {userId ? (
        <div className="border p-4 bg-gray-50" data-testid="comment-form">
          {replyTo && (
            <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
              <CornerDownRight className="h-3.5 w-3.5" />
              <span>
                {t.rich('replyingTo', {
                  name: replyTo.name,
                  who: (chunks) => <span className="font-medium text-gray-700">{chunks}</span>,
                })}
              </span>
              <button
                onClick={() => setReplyTo(null)}
                className="text-xs text-brand-600 hover:underline ml-1"
              >
                {t('cancel')}
              </button>
            </div>
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={replyTo ? t('replyPlaceholder', { name: replyTo.name }) : t('placeholder')}
            rows={3}
            className="w-full border border-gray-300 px-3 py-2 text-sm resize-none outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('reviewNote')}</p>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !body.trim()}
              size="sm"
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              <Send className="h-4 w-4 mr-1" />
              {submitting ? t('posting') : replyTo ? t('reply') : t('submit')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {t.rich('signInPrompt', {
            link: (chunks) => (
              <Link
                href="/auth?mode=login"
                className="text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:decoration-brand-700"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>
      )}
    </div>
  )
}

/**
 * Route prefixes a comment may deep-link to. Deliberately an allow-list rather
 * than "any /path": comments are prose, and a looser pattern turns everyday
 * text like "and/or" or "12/25" into links.
 */
const INTERNAL_ROUTES = [
  'tutorials', 'creating-a-scene', 'blogs', 'courses', 'floggers', 'lessons',
  'models', 'glossary', 'faq', 'store', 'getting-started', 'memberships',
  'community', 'contests', 'resources', 'safety',
].join('|')

/**
 * Absolute http(s) URLs, or a root-relative path into one of our own routes.
 * Kept as one alternation so `String.split` yields the matches as separators.
 */
const URL_PATTERN = new RegExp(
  `((?:https?://[^\\s<]+)|(?:/(?:${INTERNAL_ROUTES})(?:/[A-Za-z0-9._~%-]+)*/?))`,
)

/** Our own site — an absolute link here is internal, whatever the host. */
const SELF_HOSTS = /^https?:\/\/(?:www\.|new\.)?theduchy\.com(?=[/?#]|$)/i

function isInternalLink(url: string): boolean {
  return url.startsWith('/') || SELF_HOSTS.test(url)
}

/** Absolute self-links render as same-site paths so they route normally. */
function toInternalPath(url: string): string {
  return url.startsWith('/') ? url : url.replace(SELF_HOSTS, '') || '/'
}

function CommentBody({ text }: { text: string }) {
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault()
    const confirmed = confirm(
      'You are about to leave TheDuchy and visit an external link. ' +
      'We are not responsible for the content of external sites.\n\n' +
      `${url}\n\n` +
      'Do you want to continue?'
    )
    if (confirmed) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const parts = text.split(URL_PATTERN)

  return (
    <p className="text-sm text-gray-700 whitespace-pre-wrap">
      {parts.map((part, i) => {
        if (!part || !URL_PATTERN.test(part)) return <span key={i}>{part}</span>
        // Only genuinely off-site links get the interstitial. Warning someone
        // that they are "about to leave TheDuchy" while sending them to another
        // TheDuchy page reads as a bug, and the confirm() blocks the nav.
        if (isInternalLink(part)) {
          return (
            <a
              key={i}
              href={toInternalPath(part)}
              className="text-brand-600 underline break-all"
            >
              {part}
            </a>
          )
        }
        return (
          <a
            key={i}
            href={part}
            onClick={(e) => handleLinkClick(e, part)}
            className="text-brand-600 underline break-all"
          >
            {part}
          </a>
        )
      })}
    </p>
  )
}

function CommentThread({
  comment,
  depth,
  getDisplayName,
  onReply,
  currentUserId,
  onBlockChange,
  edit,
}: {
  comment: CommentNode
  depth: number
  getDisplayName: (c: Comment) => string
  onReply?: (id: string, name: string) => void
  currentUserId?: string | null
  onBlockChange: (userId: string, blocked: boolean) => void
  edit: CommentEdit
}) {
  const displayName = getDisplayName(comment)
  const isStaff = comment.profiles?.is_staff ?? false
  const maxIndent = 4
  const isOwn = !!currentUserId && comment.user_id === currentUserId
  const isEditing = edit.editingId === comment.id
  // Only the author's OWN pending rows reach the client (RLS), so a 'pending'
  // status here always means "your comment is awaiting moderator review".
  const isPending = comment.status === 'pending'

  return (
    <div
      className={depth > 0 ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''}
      style={depth > maxIndent ? { marginLeft: 0 } : undefined}
    >
      <div className="flex gap-3 border p-4" data-testid="comment-item">
        <div className="h-8 w-8 shrink-0 overflow-hidden bg-gray-200 flex items-center justify-center">
          {comment.profiles?.avatar_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={comment.profiles.avatar_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <User className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{displayName}</span>
            {isStaff && (
              // Staff pill — tells readers this reply is from a team
              // member (admin/moderator/content/support), not just a
              // fellow community member. Same brand palette as the
              // section accents.
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
                aria-label="Staff reply"
                title="Reply from a TheDuchy team member"
              >
                Staff
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(comment.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })}
            </span>
            {isPending && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-yellow-100 text-yellow-800"
                title="Awaiting moderator review — only you can see this until it's approved"
              >
                Pending review
              </span>
            )}
          </div>
          {isEditing ? (
            <div>
              <textarea
                value={edit.body}
                onChange={(e) => edit.onChange(e.target.value)}
                rows={3}
                autoFocus
                className="w-full border border-gray-300 px-3 py-2 text-sm resize-none outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              {edit.error && <p className="mt-1 text-xs text-red-600">{edit.error}</p>}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Edited comments are re-reviewed before publishing.</p>
              <div className="mt-1 flex items-center gap-2">
                <Button
                  onClick={() => edit.onSave(comment.id)}
                  disabled={edit.saving || !edit.body.trim()}
                  size="sm"
                  className="bg-brand-700 hover:bg-brand-800 text-white"
                >
                  {edit.saving ? 'Saving…' : 'Save'}
                </Button>
                <button
                  type="button"
                  onClick={edit.onCancel}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <CommentBody text={comment.body} />
              {comment.edited_at && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400">(edited)</span>
              )}
            </>
          )}
          {!isEditing && (
            <div className="mt-2 flex items-center gap-3">
              {onReply && (
                <button
                  onClick={() => onReply(comment.id, displayName)}
                  className="text-xs text-brand-600 hover:underline"
                >
                  Reply
                </button>
              )}
              <UgcActionsMenu
                contentType="comment"
                contentId={comment.id}
                authorUserId={comment.user_id}
                currentUserId={currentUserId}
                onBlockChange={onBlockChange}
                canEdit={isOwn}
                onEdit={() => edit.onStart(comment)}
                editLabel="Edit"
              />
            </div>
          )}
        </div>
      </div>

      {comment.children.length > 0 && (
        <div className="mt-2 space-y-2">
          {/* Depth guard: once a thread reaches MAX_COMMENT_DEPTH, render the
              whole remaining subtree FLAT (each child stripped of its own
              children so the recursion stops here) instead of nesting one more
              <CommentThread> per level. A legitimately deep conversation keeps
              every reply visible; a pathologically deep chain can no longer
              recurse past the JS call-stack limit and crash iOS/WebKit
              (THEDUCHY-WEBSITE-55). Below the cap, nesting is unchanged. */}
          {(depth + 1 >= MAX_COMMENT_DEPTH
            ? flattenReplies(comment.children).map((c) => ({ ...c, children: [] }))
            : comment.children
          ).map((child) => (
            <CommentThread
              key={child.id}
              comment={child}
              depth={depth + 1}
              getDisplayName={getDisplayName}
              onReply={onReply}
              currentUserId={currentUserId}
              onBlockChange={onBlockChange}
              edit={edit}
            />
          ))}
        </div>
      )}
    </div>
  )
}

